/**
 * GA4 Explore's tree, as AgencyAnalytics lays it out (REA pass 4): five
 * sections, 27 views, each with the same skeleton — a line chart of the
 * selected metric, a donut by the view's dimension (where it has one), one
 * card per enabled metric, and a table. Only the per-view differences live
 * here; everything else is the shared page.
 */

import type { Filter } from '../../reports/reports-api.ts';

export interface ExploreView {
  /** Address after #/client/<id>/google-analytics-4/ (AgencyAnalytics' own slugs). */
  slug: string;
  title: string;
  /** The table's dimension; several make an in-widget switch (Location: Country / Region / City). */
  table: string[];
  /** The donut's dimension (top 8 values), when the view has one. */
  donut?: string;
  /** A channel the whole view is scoped to (Organic Search, Social, …). */
  channels?: string[];
  /** The metric the line chart opens on (Sessions unless set). */
  selected?: string;
  /** A fixed card set instead of the enabled metrics (Campaigns, Ecommerce, Events). */
  cards?: string[];
}

export interface ExploreSection {
  key: string;
  title: string;
  views: ExploreView[];
}

/** AgencyAnalytics still calls key events "Conversions" on these views. */
export const CONVERSIONS_LABEL: Record<string, string> = { key_events: 'Conversions' };

const channel = (
  slug: string,
  title: string,
  channels: string[],
  table: string,
  donut?: string,
): ExploreView => ({
  slug: `channels/${slug}`,
  title,
  channels,
  table: [table],
  ...(donut ? { donut } : {}),
});

const audience = (slug: string, title: string, dimension: string, donut = true): ExploreView => ({
  slug: `audience/${slug}`,
  title,
  table: [dimension],
  ...(donut ? { donut: dimension } : {}),
});

const pages = (slug: string, title: string, dimension: string): ExploreView => ({
  slug: `pages/${slug}`,
  title,
  table: [dimension],
  selected: 'views',
});

export const GA4_SECTIONS: ExploreSection[] = [
  {
    key: 'acquisition',
    title: 'Acquisition',
    views: [
      { slug: 'channels/all', title: 'All', table: ['channel'], donut: 'channel' },
      channel('organic-search', 'Organic Search', ['Organic Search'], 'keyword', 'session_source'),
      channel('paid-search', 'Paid Search', ['Paid Search'], 'campaign', 'session_source'),
      channel('direct', 'Direct', ['Direct'], 'landing_page'),
      channel('social', 'Social', ['Organic Social'], 'session_source', 'session_source'),
      channel('referrals', 'Referral', ['Referral'], 'session_source', 'session_source'),
      channel('display', 'Display', ['Display'], 'campaign', 'session_source'),
      channel('email', 'Email', ['Email'], 'source_medium', 'session_source'),
      channel(
        'video',
        'Video',
        ['Organic Video', 'Paid Video'],
        'session_source',
        'session_source',
      ),
      channel('paid-social', 'Paid Social', ['Paid Social'], 'campaign', 'session_source'),
    ],
  },
  {
    key: 'audience',
    title: 'Audience',
    views: [
      {
        slug: 'audience/locale',
        title: 'Location',
        table: ['country', 'region', 'city'],
        donut: 'country',
      },
      audience('language', 'Language', 'language'),
      audience('age', 'Age', 'age'),
      audience('gender', 'Gender', 'gender'),
      { slug: 'audience/devices', title: 'Devices', table: ['device_model'], donut: 'device' },
      audience('browser', 'Browser', 'browser'),
      audience('operating-system', 'Operating System', 'os'),
      audience('interests', 'Interests', 'interest', false),
      audience('user-types', 'New vs Returning', 'user_type'),
    ],
  },
  {
    key: 'conversions',
    title: 'Conversions',
    views: [
      {
        slug: 'conversions/campaigns',
        title: 'Campaigns',
        table: ['campaign'],
        selected: 'total_users',
        cards: [
          'sessions',
          'total_users',
          'new_users',
          'user_engagement',
          'views',
          'key_events',
          'event_count',
        ],
      },
      {
        slug: 'conversions/ecommerce',
        title: 'Ecommerce',
        table: ['product', 'brand', 'item_category', 'item_list_id', 'item_list_name'],
        selected: 'item_revenue',
        cards: ['items_viewed', 'item_revenue', 'total_purchasers', 'items_purchased'],
      },
    ],
  },
  {
    key: 'pages',
    title: 'Pages',
    views: [
      pages('all', 'All', 'page'),
      pages('landing-pages', 'Landing Pages', 'landing_page'),
      pages('page-path-and-screen-class', 'Path', 'page_path'),
      pages('page-title-and-screen-class', 'Title', 'page_title'),
      pages('content-group', 'Content Group', 'content_group'),
    ],
  },
  {
    key: 'events',
    title: 'Events',
    views: [
      {
        slug: 'events',
        title: 'Events',
        table: ['event'],
        selected: 'event_count',
        cards: ['event_count', 'total_users', 'user_engagement', 'key_events'],
      },
    ],
  },
];

export function findView(slug: string): { section: ExploreSection; view: ExploreView } {
  for (const section of GA4_SECTIONS) {
    const view = section.views.find((candidate) => candidate.slug === slug);
    if (view) return { section, view };
  }
  const section = GA4_SECTIONS[0] as ExploreSection;
  return { section, view: section.views[0] as ExploreView };
}

/** Explore's Add Filter list (Channel Type picks the grouping rather than a value). */
export const FILTER_DIMENSIONS = [
  'country',
  'event',
  'hostname',
  'key_event',
  'landing_page',
  'os',
  'page',
  'region',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
] as const;

/** The channel groupings "Channel Type" switches between. */
export const CHANNEL_GROUPINGS = [
  'channel',
  'first_user_channel',
  'default_channel',
  'primary_channel',
  'first_user_primary_channel',
  'session_primary_channel',
] as const;

/** A view's fixed channel scope, expressed against the chosen grouping. */
export function viewFilters(view: ExploreView, grouping: string, extra: Filter[]): Filter[] {
  const scope: Filter[] = view.channels
    ? [{ dimension: grouping, op: 'is', values: view.channels }]
    : [];
  return [...scope, ...extra];
}

/** Explore's "Date Interval": how the line chart buckets time (Automatic follows the range length). */
export const DATE_INTERVALS = [
  { key: 'auto', label: 'Automatic' },
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
] as const;
export type DateInterval = (typeof DATE_INTERVALS)[number]['key'];
